import { tx as translateInterfaceText } from '@/utils/i18n';
import { useLanguage } from '@/hooks/useLanguage';
import React from 'react';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';

interface PrimeTimeCellProps {
  value: number;
  percent: number;
  weekdayLabel: string;
  bucketLabel: string;
  metricLabel: string;
  onClick: () => void;
  // Rótulo exibido na célula (compacto). Default: o próprio value.
  valueLabel?: string;
  // Valor exibido no tooltip (pode ser mais completo que o da célula). Default: valueLabel ?? value.
  tooltipValue?: string;
}

const getIntensityClasses = (percent: number) => {
  // Gradiente suave: #E9F0FF → #93C5FD → #3D7DFF
  if (percent >= 0.8) return 'bg-[#3D7DFF]';
  if (percent >= 0.6) return 'bg-[#5B9AFF]';
  if (percent >= 0.4) return 'bg-[#7AB4FF]';
  if (percent >= 0.2) return 'bg-[#93C5FD]';
  return 'bg-[#E9F0FF]';
};

const getTextColor = (percent: number) => {
  // Texto branco em tons escuros, azul marinho em claros
  if (percent >= 0.6) return 'text-white';
  return 'text-[#0F2B63]';
};

export function PrimeTimeCell({
  value,
  percent,
  weekdayLabel,
  bucketLabel,
  metricLabel,
  onClick,
  valueLabel,
  tooltipValue
}: PrimeTimeCellProps) {
  useLanguage();
  const intensityClass = getIntensityClasses(percent);
  const percentDisplay = Math.round(percent * 100);

  const textColor = getTextColor(percent);

  const cellLabel = valueLabel ?? String(value);
  const tooltipLabel = tooltipValue ?? cellLabel;

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <button
          onClick={onClick}
          role="button"
          tabIndex={0}
          aria-label={translateInterfaceText("{value0}, {value1}, {value2} {value3} ({value4}% do pico)", { value0: (weekdayLabel), value1: (bucketLabel), value2: (cellLabel), value3: (metricLabel), value4: (percentDisplay) })}
          className={cn(
            "group relative w-full h-[28px] md:h-[44px] rounded-[4px] md:rounded-[8px] transition-all duration-[120ms] ease-out",
            "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
            "flex items-center justify-center",
            "hover:ring-1 md:hover:ring-2 hover:ring-[#3273FF] hover:ring-offset-0 md:hover:ring-offset-1",
            intensityClass
          )}
        >
          <span className={cn("text-[10px] md:text-[14px] font-medium", textColor)}>
            {cellLabel}
          </span>
        </button>
      </TooltipTrigger>
      <TooltipContent 
        side="top" 
        className="max-w-xs border border-[#E5E7EB] bg-white shadow-[0_4px_10px_rgba(0,0,0,0.06)] rounded-[10px] p-0 overflow-hidden animate-in fade-in-0 duration-150"
      >
        {/* Barra colorida superior - azul para semana, lilás para fim de semana */}
        <div className={cn(
          "h-1 w-full",
          weekdayLabel.includes('Sáb') || weekdayLabel.includes('Dom') 
            ? "bg-[#8A3FFC]" 
            : "bg-[#2D66F5]"
        )} />
        
        <div className="py-[8px] px-[12px] space-y-1">
          <p className="font-medium text-[14px] text-[#0F172A]">
            {weekdayLabel} • {bucketLabel}
          </p>
          <p className="text-[13px] font-normal text-[#344054]">
            {tooltipLabel} {metricLabel}
          </p>
          <p className="text-[12px] font-normal text-[#6B7280]">
            {percentDisplay}{translateInterfaceText("% do pico")}</p>
        </div>
      </TooltipContent>
    </Tooltip>
  );
}
